import RiskScoreBadge from '../components/RiskScoreBadge';
import CodOrderVerification, {CodVerificationBadges} from '../components/CodOrderVerification';
import CodOrderRiskDetails from '../components/CodOrderRiskDetails';
import { CodOrderListState, CodBlacklistButtons, orderRowClass, CodOrderBlacklistNotice } from '../components/CodOrderListState';
import './dashboard.css';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Button,
  Card,
  DatePicker,
  Divider,
  EmptyState,
  Icon,
  IndexTable,
  InlineGrid,
  InlineStack,
  Layout,
  Modal,
  Page,
  Pagination,
  Popover,
  Select,
  Text,
  TextField,
  Tooltip,
  useIndexResourceState,
} from '../components/admin-ui';
import { CalendarIcon, SearchIcon } from '../components/admin-ui/icons';
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  c_cod_blacklist,
  d_cod_blacklist,
  q_cod_blacklists,
  q_orders,
} from "../api";
import storageUtils from "../utils/storageUtils";
import OrderTransferDialogs from "../components/OrderTransferDialogs";
import CodFulfillmentHistory from "../components/CodFulfillmentHistory";
import { orderTransferStatus } from "../api/orderTransfers";
import { useSearchParams } from 'react-router-dom';

const formatDateForQuery = (date) => {
  if (!date) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatOrderDate = (timestamp) => {
  const date = new Date(Number(timestamp) * 1000);
  if (Number.isNaN(date.getTime())) return "–";
  return `${date.getUTCFullYear()}-${String(date.getUTCMonth() + 1).padStart(2, "0")}-${String(date.getUTCDate()).padStart(2, "0")} ${String(date.getUTCHours()).padStart(2, "0")}:${String(date.getUTCMinutes()).padStart(2, "0")} UTC`;
};

const formatMoney = (value, currency, formatted = false) => {
  if (value === null || value === undefined || value === "") return "–";
  if (!currency) return "Currency not recorded";
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "–";
  try { return new Intl.NumberFormat("en", { style: "currency", currency, currencyDisplay: "code" }).format(amount); } catch (_) { return `${value} ${currency}`; }
};

const addressText = (address = {}) => [
  address.address1 || address.address_line1,
  address.address2 || address.address_line2,
  address.city,
  address.province || address.state,
  address.zip || address.postal_code,
  address.country || address.country_code,
].filter(Boolean).join(", ") || "No address";

const itemTitle = (item = {}) => item.product_title || item.title || item.name || "Product";

const messageFromError = (error, fallback) => {
  const body = error?.response?.data || {};
  const errors = body.errors || body.data?.errors;
  const first = errors ? Object.values(errors).flat()[0] : null;
  return first || body.msg || body.message || fallback;
};

const riskBadge = (order) => <InlineStack gap="100"><RiskScoreBadge order={order} /><CodOrderBlacklistNotice order={order} /></InlineStack>;

const statusBadge = (status) => {
  const map = {
    paid: { tone: "success", label: "Paid" },
    pending: { tone: "attention", label: "Pending" },
    processing: { tone: "info", label: "Processing" },
    failed: { tone: "critical", label: "Failed" },
  };
  const item = map[status] || { tone: undefined, label: status || "Unknown" };
  return <Badge tone={item.tone}>{item.label}</Badge>;
};

function MetricCard({ label, value, tone, onClick }) {
  return (
    <Card>
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="p" tone="subdued">{label}</Text>
          {tone ? <Badge tone={tone}>{label}</Badge> : null}
        </InlineStack>
        <InlineStack align="space-between" blockAlign="end">
          <Text as="p" variant="heading2xl">{value ?? 0}</Text>
          {onClick ? <Button variant="plain" onClick={onClick}>View</Button> : null}
        </InlineStack>
      </BlockStack>
    </Card>
  );
}

export default function DashBoard() {
  const [searchParams, setSearchParams] = useSearchParams();
  const userToken = storageUtils.get("user_token");
  const [orders, setOrders] = useState([]);
  const orderRequest = useRef(0);
  const { selectedResources, allResourcesSelected, handleSelectionChange, clearSelection } = useIndexResourceState(orders, { resourceIDResolver: order => order.key });
  const [transferMode, setTransferMode] = useState(null);
  useEffect(() => {
    const requested = searchParams.get('transfer');
    if (!['export', 'import', 'tasks'].includes(requested)) return;
    setTransferMode(requested);
    const next = new URLSearchParams(searchParams); next.delete('transfer');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [transferStatus, setTransferStatus] = useState({ counts: {} });
  const [stats, setStats] = useState({ all: 0, cod: 0, high_risk: 0, blacklists: 0 });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState("");
  const [blacklistPending, setBlacklistPending] = useState({});
  const blacklistRequests = useRef(new Set());
  const [notice, setNotice] = useState(null);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [orderType, setOrderType] = useState("all");
  const [riskLevel, setRiskLevel] = useState("all");
  const [fulfillmentHistoryEnabled, setFulfillmentHistoryEnabled] = useState(null);
  const [fulfillmentHistoryFilter, setFulfillmentHistoryFilter] = useState("all");
  const [historyFilterNotice, setHistoryFilterNotice] = useState(null);
  const [searchDraft, setSearchDraft] = useState("");
  const [search, setSearch] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [blacklistsOpen, setBlacklistsOpen] = useState(false);
  const [blacklists, setBlacklists] = useState([]);
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const [selectedMonthYear, setSelectedMonthYear] = useState({
    month: new Date().getUTCMonth(),
    year: new Date().getUTCFullYear(),
  });
  const [selectedDates, setSelectedDates] = useState({
    start: new Date(new Date().setUTCDate(new Date().getUTCDate() - 7)),
    end: new Date(),
  });
  const limit = 20;
  const transferFilters = useMemo(() => ({ start_date: formatDateForQuery(selectedDates.start), end_date: formatDateForQuery(selectedDates.end), order_type: orderType, risk_level: riskLevel, search }), [selectedDates, orderType, riskLevel, search]);
  const refreshTransferStatus = useCallback(() => {
    if (userToken) orderTransferStatus({ user_token: userToken }).then(setTransferStatus).catch(() => {});
  }, [userToken]);
  useEffect(() => { refreshTransferStatus(); }, [refreshTransferStatus]);
  useEffect(() => { clearSelection(); }, [page, transferFilters, fulfillmentHistoryFilter, clearSelection]);

  const queryOrders = useCallback(async () => {
    if (!userToken) return;
    const request = ++orderRequest.current;
    setLoading(true);
    try {
      const response = await q_orders({
        user_token: userToken,
        page,
        limit,
        start_date: formatDateForQuery(selectedDates.start),
        end_date: formatDateForQuery(selectedDates.end),
        order_type: orderType,
        risk_level: riskLevel,
        fulfillment_history_filter: fulfillmentHistoryFilter,
        search,
      });
      if (request !== orderRequest.current) return;
      const data = response.data || {};
      if (riskLevel !== 'all' && data.risk_filter_status === 'unavailable') {
        setNotice({ tone: 'warning', message: 'The risk score filter is unavailable. Showing orders without this filter.' });
        setRiskLevel('all'); setPage(1);
      }
      setFulfillmentHistoryEnabled(typeof data.fulfillment_history_enabled === 'boolean' ? data.fulfillment_history_enabled : null);
      if (fulfillmentHistoryFilter !== 'all' && (data.fulfillment_history_filter_status !== 'applied'
        || data.fulfillment_history_filter !== fulfillmentHistoryFilter)) {
        setHistoryFilterNotice(data.fulfillment_history_filter_status === 'disabled'
          ? 'Delivery history reminders are disabled. Showing orders without the delivery history filter.'
          : 'The delivery history filter is unavailable. Showing orders without this filter.');
        setFulfillmentHistoryFilter('all'); setPage(1);
      }
      setOrders(data.list || []);
      setSelectedOrder(current => current ? (data.list || []).find(item => item.key === current.key)
        || current : null);
      setTotalCount(response.data?.total || 0);
      setLastPage(response.data?.last_page || 1);
      setStats((current) => ({ ...current, ...(response.data?.stats || {}) }));
    } catch (error) {
      if (request === orderRequest.current && error?.code !== 'ERR_CANCELED' && !error?.__CANCEL__) {
        const errors = error?.response?.data?.errors || error?.response?.data?.data?.errors;
        if (fulfillmentHistoryFilter !== 'all' && errors?.fulfillment_history_filter) {
          setHistoryFilterNotice('The delivery history filter is unavailable. Showing orders without this filter.');
          setFulfillmentHistoryFilter('all'); setPage(1);
          return;
        }
        setNotice({ tone: "critical", message: messageFromError(error, "Unable to load orders.") });
      }
    } finally {
      if (request === orderRequest.current) setLoading(false);
    }
  }, [orderType, page, riskLevel, fulfillmentHistoryFilter, search, selectedDates, userToken]);

  // Fast workers may finish before the first queued status poll. Refresh the
  // order rows when terminal counts change as well as during queued polling.
  useEffect(() => { queryOrders(); }, [queryOrders, transferStatus.counts?.synced, transferStatus.counts?.failed]);
  const syncingOrders = Number(transferStatus.counts?.queued || 0) + Number(transferStatus.counts?.processing || 0);
  useEffect(() => {
    if (!syncingOrders) return;
    const timer = setInterval(() => { refreshTransferStatus(); queryOrders(); }, 10000);
    return () => clearInterval(timer);
  }, [syncingOrders, refreshTransferStatus, queryOrders]);

  const changeFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const createBlacklist = async (order, subject) => {
    const label = subject === "ip" ? `IP ${order.ip}` : "this customer’s phone and email";
    if (!window.confirm(`Add ${label} to the COD blacklist? Future COD orders will be blocked.`)) return;
    const key = `${subject}:${order.id}`;
    if (blacklistRequests.current.has(key)) return;
    blacklistRequests.current.add(key);
    try {
      setBlacklistPending(current => ({ ...current, [key]: true }));
      setNotice(null);
      const response = await c_cod_blacklist({
        user_token: userToken,
        cod_order_id: order.id,
        subject,
      });
      if (!response.data?.list_membership || Number(response.status ?? response.code ?? 200) !== 200) throw new Error('Unable to update the blacklist.');
      const changed = response.data;
      const confirmed = { list_membership: changed.list_membership, ...(changed.risk_assessment ? {
        risk_assessment: changed.risk_assessment, risk_score: changed.risk_assessment.risk_score, risk_reasons: changed.risk_assessment.reasons,
      } : {}) };
      setOrders(current => current.map(item => item.type === 'cod' && item.id === changed.cod_order_id ? { ...item, ...confirmed } : item));
      setSelectedOrder(current => current?.type === 'cod' && current.id === changed.cod_order_id ? { ...current, ...confirmed } : current);
      if (changed.stats) setStats(current => ({ ...current, ...changed.stats }));
      setNotice({ tone: "success", message: response.msg || "Blacklist updated." });
      await queryOrders();
    } catch (error) {
      setNotice({ tone: "critical", message: messageFromError(error, "Unable to update the blacklist.") });
    } finally {
      blacklistRequests.current.delete(key);
      setBlacklistPending(current => ({ ...current, [key]: false }));
    }
  };

  const updateRiskAssessment = async changed => {
    const apply = current => current?.type === 'cod' && current.id === changed.cod_order_id
      ? { ...current, risk_assessment: changed.risk_assessment, risk_score: changed.risk_assessment.risk_score, risk_reasons: changed.risk_assessment.reasons } : current;
    setOrders(current => current.map(apply));
    setSelectedOrder(apply);
    await queryOrders();
  };

  const openBlacklists = async () => {
    setBlacklistsOpen(true);
    setActionLoading("blacklists");
    try {
      const response = await q_cod_blacklists({ user_token: userToken });
      setBlacklists(response.data?.list || []);
    } catch (error) {
      setNotice({ tone: "critical", message: messageFromError(error, "Unable to load the blacklist.") });
    } finally {
      setActionLoading("");
    }
  };

  const removeBlacklist = async (entry) => {
    try {
      setActionLoading(`remove-blacklist:${entry.id}`);
      const response = await d_cod_blacklist({ user_token: userToken, id: entry.id, ...(selectedOrder?.type === 'cod' ? { cod_order_id: selectedOrder.id } : {}) });
      if (Number(response.status ?? response.code ?? 200) !== 200) throw new Error('Unable to remove the blacklist entry.');
      setBlacklists((current) => current.filter((item) => item.id !== entry.id));
      if (response.data?.stats) setStats(current => ({ ...current, ...response.data.stats }));
      if (response.data?.list_membership) setSelectedOrder(current => current?.id === response.data.cod_order_id ? {
        ...current, list_membership: response.data.list_membership, ...(response.data.risk_assessment ? {
          risk_assessment: response.data.risk_assessment, risk_score: response.data.risk_assessment.risk_score, risk_reasons: response.data.risk_assessment.reasons,
        } : {}),
      } : current);
      await queryOrders();
      setNotice({ tone: "success", message: response.msg || "Blacklist entry removed." });
    } catch (error) {
      setNotice({ tone: "critical", message: messageFromError(error, "Unable to remove the blacklist entry.") });
    } finally {
      setActionLoading("");
    }
  };

  const showOrder = useCallback((order) => {
    setSelectedOrder({ ...order, list_membership: { status: 'unavailable' } });
    queryOrders();
  }, [queryOrders]);

  const rows = useMemo(() => orders.map((order, index) => (
    <IndexTable.Row id={order.key} key={order.key} position={index} selected={selectedResources.includes(order.key)} className={orderRowClass(order)}>
      <IndexTable.Cell>
        <BlockStack gap="100">
          <Text fontWeight="bold" as="span">{order.name}</Text>
          {order.shopify_order_number ? <span className="cod-order-number">{order.shopify_order_number}</span> : null}
          {order.source_shop ? <Text as="span" tone="subdued">Source: {order.source_shop}</Text> : null}
        </BlockStack>
      </IndexTable.Cell>
      <IndexTable.Cell>
        <BlockStack gap="100">
          <Text as="span">{order.customer || "N/A"}</Text>
          {order.phone ? <Text as="span" tone="subdued">{order.phone}</Text> : null}
        </BlockStack>
      </IndexTable.Cell>
      <IndexTable.Cell><Text as="span">{order.ip || "—"}</Text></IndexTable.Cell>
      <IndexTable.Cell><Box maxWidth="240px"><Text as="span">{addressText(order.address)}</Text></Box></IndexTable.Cell>
      <IndexTable.Cell>
        <Box maxWidth="220px">
          <Tooltip width="wide" content={(
            <span style={{ whiteSpace: "normal", overflowWrap: "anywhere" }}>
              {(order.items || []).map(itemTitle).join(", ") || "No items"}
            </span>
          )}>
            <div tabIndex={0} style={{ maxWidth: "220px" }}>
              <Text as="p" truncate>{(order.items || []).map(itemTitle).join(", ") || "No items"}</Text>
            </div>
          </Tooltip>
        </Box>
      </IndexTable.Cell>
      <IndexTable.Cell><BlockStack gap="100"><Text as="span" fontWeight="bold">{formatMoney(order.total, order.currency)}</Text><Text as="span" tone="subdued">{order.collection_due ? "To collect" : (order.payment_confirmed || order.status === "paid") ? "Paid" : "Order amount"}</Text></BlockStack></IndexTable.Cell>
      <IndexTable.Cell><BlockStack gap="200">{riskBadge(order)}<CodFulfillmentHistory order={order} enabled={fulfillmentHistoryEnabled} /></BlockStack></IndexTable.Cell>
      <IndexTable.Cell>{formatOrderDate(order.create_time)}</IndexTable.Cell>
      <IndexTable.Cell><BlockStack gap="100">{statusBadge(order.status)}<CodVerificationBadges summary={order.verification} />{order.shipment?.tracking_number ? <>
        <Text as="span">{order.shipment.tracking_number}</Text><Badge tone={order.shipment.sync_status === 'synced' ? 'success' : order.shipment.sync_status === 'failed' ? 'critical' : 'attention'}>{`Shopify: ${order.shipment.sync_status}`}</Badge>
      </> : null}</BlockStack></IndexTable.Cell>
      <IndexTable.Cell><Button variant="plain" onClick={() => showOrder(order)}>View</Button></IndexTable.Cell>
    </IndexTable.Row>
  )), [orders, selectedResources, fulfillmentHistoryEnabled, showOrder]);

  return (
    <div className="order-dashboard"><Page title="Orders Management" subtitle="Online and cash-on-delivery orders in one operational view. Times are shown in UTC." fullWidth
      primaryAction={{ content: "Export orders", onAction: () => setTransferMode("export"), disabled: fulfillmentHistoryFilter !== "all" }}
      secondaryActions={[{ content: "Import tracking numbers", onAction: () => setTransferMode("import") }, { content: "Sync & transfer history", onAction: () => setTransferMode("tasks") }]}>
      <BlockStack gap="500">
        {notice ? <Banner tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.message}</Banner> : null}
        {historyFilterNotice ? <Banner tone="warning" onDismiss={() => setHistoryFilterNotice(null)}>{historyFilterNotice}</Banner> : null}
        {fulfillmentHistoryFilter !== 'all' ? <Text as="p" tone="subdued">Select orders to export while a delivery history filter is applied.</Text> : null}
        {Object.entries(transferStatus.counts || {}).some(([state, count]) => state !== 'synced' && Number(count) > 0) ? <Banner tone="info" action={{ content: "Review Shopify sync tasks", onAction: () => setTransferMode("tasks") }}>
          Tracking saved locally: {transferStatus.counts.pending || 0} pending sync, {Number(transferStatus.counts.queued || 0) + Number(transferStatus.counts.processing || 0)} queued / processing, {transferStatus.counts.failed || 0} failed. Submit pending tasks to fulfill orders in Shopify.
        </Banner> : null}

        <InlineGrid columns={{ xs: 1, sm: 2, lg: 4 }} gap="300">
          <MetricCard label="All orders" value={stats.all} onClick={() => { setOrderType("all"); setRiskLevel("all"); setFulfillmentHistoryFilter("all"); setHistoryFilterNotice(null); setPage(1); }} />
          <MetricCard label="COD orders" value={stats.cod} tone="attention" onClick={() => { setOrderType("cod"); setPage(1); }} />
          <MetricCard label="Score 60+" value={stats.high_risk === null ? '—' : stats.high_risk} tone="critical" onClick={() => { setOrderType("cod"); setRiskLevel("high"); setPage(1); }} />
          <MetricCard label="Blacklisted" value={stats.blacklists} onClick={openBlacklists} />
        </InlineGrid>

        <Layout>
          <Layout.Section>
            <Card>
              <BlockStack gap="400">
                <InlineGrid columns={{ xs: 1, md: 4 }} gap="300">
                  <TextField
                    label="Search orders"
                    labelHidden
                    value={searchDraft}
                    onChange={setSearchDraft}
                    onKeyPress={(event) => { if (event.key === "Enter") { setSearch(searchDraft.trim()); setPage(1); } }}
                    prefix={<Icon source={SearchIcon} />}
                    placeholder="Order, email, phone or IP"
                    autoComplete="off"
                    connectedRight={<Button onClick={() => { setSearch(searchDraft.trim()); setPage(1); }}>Search</Button>}
                  />
                  <Select label="Order type" labelHidden options={[{ label: "All order types", value: "all" }, { label: "Online", value: "online" }, { label: "Cash on delivery", value: "cod" }]} value={orderType} onChange={(value) => { setOrderType(value); if (value === "online") { setRiskLevel("all"); setFulfillmentHistoryFilter("all"); setHistoryFilterNotice(null); } setPage(1); }} />
                  <Select label="Risk score" labelHidden options={[{ label: "All scores", value: "all" }, { label: "Score 0–14", value: "low" }, { label: "Score 15–59", value: "medium" }, { label: "Score 60+", value: "high" }]} value={riskLevel} onChange={changeFilter(setRiskLevel)} disabled={orderType === "online"} />
                  <Select label="Delivery history" labelHidden options={[
                    { label: "All delivery histories", value: "all" },
                    { label: "Matching address: refusal history", value: "has_refusals" },
                    { label: "Matching address: delivery history", value: "has_deliveries" },
                    { label: "No confirmed phone and address history", value: "no_history" },
                  ]} value={fulfillmentHistoryFilter} onChange={value => { setFulfillmentHistoryFilter(value); setHistoryFilterNotice(null); if (value !== 'all') setOrderType('cod'); setPage(1); }} disabled={fulfillmentHistoryEnabled !== true || orderType === "online"} />
                </InlineGrid>
                <InlineStack align="space-between" blockAlign="center" gap="300" wrap>
                  <Text as="p" tone="subdued">{totalCount} matching orders</Text>
                  <InlineStack gap="200">
                    <Popover
                      active={isDatePickerOpen}
                      activator={<Button onClick={() => setDatePickerOpen(!isDatePickerOpen)} icon={CalendarIcon}>{selectedDates.start ? `${formatDateForQuery(selectedDates.start)} – ${formatDateForQuery(selectedDates.end)}` : "All time"}</Button>}
                      onClose={() => setDatePickerOpen(false)}
                    >
                      <Box padding="400">
                        <DatePicker
                          month={selectedMonthYear.month}
                          year={selectedMonthYear.year}
                          onChange={(range) => { setSelectedDates(range); setPage(1); }}
                          onMonthChange={(month, year) => setSelectedMonthYear({ month, year })}
                          selected={selectedDates.start ? selectedDates : undefined}
                          allowRange
                          disableDatesAfter={new Date()}
                        />
                      </Box>
                    </Popover>
                    <Button variant="tertiary" onClick={() => { setSelectedDates({ start: null, end: null }); setPage(1); }}>Show all dates</Button>
                  </InlineStack>
                </InlineStack>
              </BlockStack>
            </Card>

            <Box paddingBlockStart="400">
              <Card padding="0">
                {orders.length === 0 && !loading ? (
                  <EmptyState heading="No orders match these filters" image="">
                    <p>Try another date range, order type, risk score or search.</p>
                  </EmptyState>
                ) : (
                  <IndexTable
                    styledRows
                    resourceName={{ singular: "order", plural: "orders" }}
                    itemCount={orders.length}
                    headings={[
                      { title: "Order" },
                      { title: "Customer" },
                      { title: "IP address" },
                      { title: "Address" },
                      { title: "Items" },
                      { title: "Order amount / currency" },
                      { title: "Risk score" },
                      { title: "Date (UTC)" },
                      { title: "Status" },
                      { id: "actions", title: "Actions" },
                    ]}
                    selectable
                    selectedItemsCount={allResourcesSelected ? 'All' : selectedResources.length}
                    onSelectionChange={handleSelectionChange}
                    promotedBulkActions={[{ content: `Export selected (${selectedResources.length})`, onAction: () => setTransferMode('export') }]}
                    loading={loading}
                  >
                    {rows}
                  </IndexTable>
                )}
                <Box padding="400">
                  <InlineStack align="center">
                    <Pagination hasPrevious={page > 1} onPrevious={() => setPage((value) => value - 1)} hasNext={page < lastPage} onNext={() => setPage((value) => value + 1)} label={`Page ${page} of ${lastPage}`} />
                  </InlineStack>
                </Box>
              </Card>
            </Box>
          </Layout.Section>
        </Layout>
      </BlockStack>

      <Modal open={Boolean(selectedOrder)} onClose={() => setSelectedOrder(null)} title={selectedOrder?.name || "Order details"}>
        <Modal.Section>
          {selectedOrder ? (
            <BlockStack gap="400">
              <InlineStack gap="200" blockAlign="center">
                <Badge tone={selectedOrder.type === "cod" ? "attention" : "info"}>{selectedOrder.type === "cod" ? "COD" : "Online"}</Badge>
                {riskBadge(selectedOrder)}
                {statusBadge(selectedOrder.status)}
              </InlineStack>
              <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
                <BlockStack gap="100"><Text as="p" tone="subdued">Customer</Text><Text as="p">{selectedOrder.email || selectedOrder.phone || "N/A"}</Text>{selectedOrder.phone ? <Text as="p">{selectedOrder.phone}</Text> : null}</BlockStack>
                <BlockStack gap="100"><Text as="p" tone="subdued">Placed</Text><Text as="p">{formatOrderDate(selectedOrder.create_time)}</Text></BlockStack>
                <BlockStack gap="100"><Text as="p" tone="subdued">Delivery address</Text><Text as="p">{addressText(selectedOrder.address)}</Text></BlockStack>
                <BlockStack gap="100"><Text as="p" tone="subdued">IP address</Text><Text as="p">{selectedOrder.ip || "–"}</Text></BlockStack>
              </InlineGrid>
              <Text as="p" variant="headingLg">{formatMoney(selectedOrder.total, selectedOrder.currency)} · {selectedOrder.collection_due ? "To collect" : (selectedOrder.payment_confirmed || selectedOrder.status === "paid") ? "Paid" : "Order amount"}</Text>
              {selectedOrder.source_shop ? <Text as="p">Source store: {selectedOrder.source_shop}</Text> : null}
              <PriceSnapshot order={selectedOrder} />
              {selectedOrder.shipment ? <BlockStack gap="200"><Text as="h3" variant="headingSm">Shipment & tracking</Text>
                <Text as="p">{selectedOrder.shipment.reference}</Text><Text as="p">{selectedOrder.shipment.tracking_company} {selectedOrder.shipment.tracking_number || 'Tracking not imported'}</Text>
                <Text as="p">Shopify sync: {selectedOrder.shipment.sync_status}</Text>
                {selectedOrder.shipment.sync_error && <Banner tone="warning">{selectedOrder.shipment.sync_error}</Banner>}
              </BlockStack> : null}
              <Divider />
              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">Items</Text>
                {(selectedOrder.items || []).map((item, index) => (
                  <InlineStack key={`${itemTitle(item)}-${index}`} align="space-between" gap="300">
                    <Text as="p">{itemTitle(item)} × {item.quantity || 1}</Text>
                    <Text as="p">{formatMoney(item.unit_price ?? item.price ?? item.variant_price, selectedOrder.currency, selectedOrder.type === "cod" || selectedOrder.price_unit === "major")}</Text>
                  </InlineStack>
                ))}
              </BlockStack>
              {selectedOrder.type === "cod" ? (
                <>
                  {selectedOrder.cod_upsell_status && <Banner tone={selectedOrder.cod_upsell_status === "completed" ? "success" : "info"}>
                    {selectedOrder.cod_upsell_status === "completed" ? "COD add-ons are included in this order total."
                      : selectedOrder.cod_upsell_status === "pending" ? "COD add-ons are being added. Refresh the order to see its updated total."
                        : "The COD add-on update could not be confirmed. Check the Shopify order before making manual changes."}
                  </Banner>}
                  <Divider />
                  <InlineGrid columns={{ xs: 1, sm: 3 }} gap="300">
                    <BlockStack gap="100"><Text as="p" tone="subdued">Shipping</Text><Text as="p">{formatMoney(selectedOrder.shipping_amount, selectedOrder.currency, selectedOrder.type === "cod")}</Text></BlockStack>
                    <BlockStack gap="100"><Text as="p" tone="subdued">Discount</Text><Text as="p">{selectedOrder.discount_code ? `${selectedOrder.discount_code} · −${formatMoney(selectedOrder.discount_amount, selectedOrder.currency, selectedOrder.type === "cod")}` : "None"}</Text></BlockStack>
                    <BlockStack gap="100"><Text as="p" tone="subdued">Total</Text><Text as="p" fontWeight="bold">{formatMoney(selectedOrder.total, selectedOrder.currency, selectedOrder.type === "cod")}</Text></BlockStack>
                  </InlineGrid>
                  <CodOrderVerification key={`verification:${selectedOrder.key}`} order={selectedOrder} userToken={userToken} onChanged={summary=>{
                    const key=selectedOrder.key;
                    setOrders(current=>current.map(o=>o.key===key?{...o,verification:summary}:o));
                    setSelectedOrder(current=>current?.key===key?{...current,verification:summary}:current);
                  }} />
                  {selectedOrder.failure_code === 'invalid_phone' && <Banner tone="critical">Invalid phone number. Shopify could not create the order. Check the number and country calling code.</Banner>}
                  <CodOrderListState order={selectedOrder} />
                  <CodOrderRiskDetails key={selectedOrder.key} order={selectedOrder} userToken={userToken} onChanged={updateRiskAssessment} />
                  <CodFulfillmentHistory order={selectedOrder} enabled={fulfillmentHistoryEnabled} detailed />
                  {selectedOrder.risk_reasons?.length ? <Banner tone="warning">{selectedOrder.risk_reasons.join(" · ")}</Banner> : null}
                  <InlineStack gap="200" wrap>
                    <CodBlacklistButtons order={selectedOrder} pending={blacklistPending} onAdd={createBlacklist} />
                  </InlineStack>
                </>
              ) : null}
            </BlockStack>
          ) : null}
        </Modal.Section>
      </Modal>

      <Modal open={blacklistsOpen} onClose={() => setBlacklistsOpen(false)} title="COD blacklist">
        <Modal.Section>
          {actionLoading === "blacklists" ? (
            <Text as="p" tone="subdued">Loading blacklist…</Text>
          ) : blacklists.length === 0 ? (
            <Text as="p" tone="subdued">No active IP or customer blacklist entries.</Text>
          ) : (
            <BlockStack gap="300">
              {blacklists.map((entry) => (
                <Card key={entry.id} background="bg-surface-secondary">
                  <InlineStack align="space-between" blockAlign="center" gap="300" wrap={false}>
                    <BlockStack gap="100">
                      <InlineStack gap="200" blockAlign="center">
                        <Badge>{Number(entry.subject_type) === 1 ? "IP" : Number(entry.subject_type) === 2 ? "Phone" : Number(entry.subject_type) === 4 ? "Domain" : "Email"}</Badge>
                        <Text as="p" fontWeight="semibold">{entry.subject_masked || "Hidden"}</Text>
                      </InlineStack>
                      {entry.reason ? <Text as="p" tone="subdued">{entry.reason}</Text> : null}
                      <Text as="p" tone="subdued">{entry.expires_at ? `Expires ${formatOrderDate(entry.expires_at)}` : "No expiration"}</Text>
                    </BlockStack>
                    <Button variant="plain" tone="critical" onClick={() => removeBlacklist(entry)} loading={actionLoading === `remove-blacklist:${entry.id}`}>Remove</Button>
                  </InlineStack>
                </Card>
              ))}
            </BlockStack>
          )}
        </Modal.Section>
      </Modal>
      <OrderTransferDialogs mode={transferMode} onClose={() => setTransferMode(null)} userToken={userToken}
        selectedKeys={selectedResources} selectedOnly={fulfillmentHistoryFilter !== 'all'} filters={transferFilters} onStatus={setTransferStatus}
        onImported={() => { refreshTransferStatus(); queryOrders(); }} />
    </Page></div>
  );
}

function PriceSnapshot({ order }) {
  const snapshot = order.price_snapshot;
  if (!snapshot) return <Text as="p" tone="subdued">Price snapshot not recorded for this order. Historical rates are not recalculated.</Text>;
  const conversion = snapshot.conversion;
  const customer = conversion ? {currency: conversion.currency, total: conversion.total, shipping: {amount: conversion.shipping_amount},
    discount: {amount: conversion.discount_amount}, upsell_subtotal: conversion.before_upsell_subtotal,
    currency_context: {base_currency: snapshot.currency, rate: conversion.rate}} : null;
  const currency = customer?.currency || snapshot.currency;
  const quote = customer || snapshot;
  return <Card><BlockStack gap="200">
    <Text as="h3" variant="headingSm">Price at order confirmation</Text>
    <Text as="p">Original total: {formatMoney(quote.total, currency)}</Text>
    {snapshot.breakdown_available === false ? <Text as="p" tone="subdued">Payment confirmed. A unique shipping and discount breakdown could not be linked to this payment.</Text> : <Text as="p">Shipping: {formatMoney(quote.shipping?.amount ?? 0, currency)} · Discount: {formatMoney(quote.discount?.amount ?? 0, currency)}</Text>}
    {customer?.currency_context ? <Text as="p">Rate at order: 1 {customer.currency_context.base_currency} = {customer.currency_context.rate} {currency}</Text> : null}
    {quote.upsell_subtotal || snapshot.before_upsell_subtotal ? <Text as="p">Before upsell included: {formatMoney(quote.upsell_subtotal ?? snapshot.before_upsell_subtotal, currency)}</Text> : null}
    {(order.price_additions || []).map((addition, index) => <Text as="p" key={index}>After upsell #{index + 1}: +{formatMoney(addition.customer_additional_amount ?? addition.additional_amount, addition.customer_currency ?? addition.currency)}</Text>)}
    <Text as="p" tone="subdued">Saved prices remain unchanged when exchange rates or promotion settings change.</Text>
  </BlockStack></Card>;
}
